import type { IAuditChange } from "../../../interfaces/models/IAudityLogs";
import {
  AuditEntityType,
  AuditEntityTypeEnum,
} from "../../../Enums/LogsEnum";

function formatRoleLabel(role: string): string {
  const trimmed = String(role ?? "").trim();
  if (!trimmed) {
    return "";
  }
  const spaced = trimmed.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export type FirmAuditLogNoteInput = {
  entity: string;
  action: string;
  entityId: string;
  changes?: IAuditChange[];
  firmName?: string;
};

type ChangeEntry = { oldValue: unknown; newValue: unknown };

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const NOISE_FIELDS = new Set([
  "id",
  "_ts",
  "_rid",
  "_self",
  "_etag",
  "_attachments",
  "createdDate",
  "userId",
  "userName",
  "firm",
  "mainOffice",
  "additionalOffices",
  "memberType",
  "isSearchable",
  "isAttorney",
  "socialNetworks",
  "areasOfPractice",
  "aopExpertiseIds",
  "submittedBy",
  "role",
  "externalType",
  "mappedMainOffice",
  "mappedMainOfficeAdditional",
  "photo",
  "pk",
]);

const READABLE_UPDATE_FIELDS: Record<string, string> = {
  name: "name",
  lastName: "last name",
  title: "title",
  firmEmail: "email",
  firmName: "firm name",
  mainOfficeName: "main office",
  unlistedTitle: "unlisted title",
  invoiceNumber: "invoice number",
  reference: "reference",
  status: "status",
  amount: "amount",
  memo: "memo",
};

const FIRM_UPDATE_FIELD_LABELS: Record<string, string> = {
  ...READABLE_UPDATE_FIELDS,
  membershipType: "membership type",
  membershipStatus: "membership status",
  phoneNumber: "phone number",
  websiteUrl: "website",
  firmOverview: "firm overview",
  serviceType: "service type",
  otherServiceType: "other service type",
  jurisdiction: "jurisdiction",
  jurisdictionGroup: "jurisdiction group",
  country: "country",
  state: "state",
  city: "city",
  yearFounded: "year founded",
  listingCategory: "listing category",
  practiceIndustry: "practice industry",
  isMember: "is member",
  shouldHideInStats: "hide in stats",
  mustExclude: "must exclude from stats",
  linkedinUrl: "LinkedIn URL",
  xUrl: "X URL",
  instagramUrl: "Instagram URL",
  facebookUrl: "Facebook URL",
  youtubeUrl: "YouTube URL",
  otherSocialMedia: "other social media",
  logo: "logo",
  smallLogo: "small logo",
  dateJoined: "date joined",
  dateWithdrawn: "date withdrawn",
  dateCreated: "date created",
  totalProfessionals: "total professionals",
  firmProfessionalsInPrimary: "professionals in primary",
  firmProfessionalsWorldWide: "professionals worldwide",
  acceptedEmailDomain: "accepted email domain",
  regionNames: "region",
  region: "regions",
  address: "address",
  invoiceAddress: "invoice address",
  firmContact: "firm contact",
  firmContactStructure: "firm contact structure",
  aopIds: "areas of practice",
  hasSecondary: "has secondary",
  secondaryJurisdiction: "secondary jurisdiction",
  shortId: "ID",
  notable1: "notable 1",
  notable2: "notable 2",
  notable3: "notable 3",
  notable4: "notable 4",
  notableImage1: "notable image 1",
  notableImage2: "notable image 2",
  notableImage3: "notable image 3",
  notableImage4: "notable image 4",
  officesUpdated: "offices updated",
  groupCreated: "group created",
};

const PERSON_UPDATE_FIELD_LABELS: Record<string, string> = {
  ...FIRM_UPDATE_FIELD_LABELS,
  middleInitial: "middle initial",
  suffix: "suffix",
  honorary: "honorary",
  listTitle: "list title",
  bio: "bio",
  languages: "languages",
  barAdmissions: "bar admissions",
  generatedId: "professional ID",
  mainContact: "main contact",
  hasAssistant: "has assistant",
  assistantName: "assistant name",
  assistantEmail: "assistant email",
  directPhoneNumber: "direct phone",
  countryCodePhoneNumber: "country code phone",
  directCountryCodePhoneNumber: "direct country code phone",
  significantAccomplishments: "significant accomplishments",
  industryOrganizationAffiliation: "industry affiliations",
  additionalActivities: "additional activities",
  seminarPresentation: "seminar presentation",
  additionalInformation: "additional information",
  legacyId: "legacy ID",
};

const OFFICE_UPDATE_FIELD_LABELS: Record<string, string> = {
  ...FIRM_UPDATE_FIELD_LABELS,
  officeType: "office type",
  cityName: "city",
  countryName: "country",
  stateName: "state",
  regionName: "region",
  overview: "overview",
  showInDirectory: "show in directory",
  inPrimaryJurisdiction: "in primary jurisdiction",
  moreInformation: "more information",
  countOfPeople: "count of people",
  billingStartDate: "billing start date",
  balance: "balance",
  faxNumber: "fax number",
};

const DATA_ADMIN_UPDATE_FIELD_LABELS: Record<string, string> = {
  ...READABLE_UPDATE_FIELDS,
  firmId: "firm ID",
  personId: "person ID",
  personName: "person name",
  officeId: "office ID",
  firmEmail: "email",
  lastLogin: "last login",
  membershipLocation: "membership location",
  membershipLocationName: "membership location name",
  subscription: "subscription",
};

const CONTACT_UPDATE_FIELD_LABELS: Record<string, string> = {
  ...READABLE_UPDATE_FIELDS,
  email: "email",
  phoneNumber: "phone number",
  contactType: "contact type",
  isPrimary: "is primary",
  officeId: "office ID",
  firmId: "firm ID",
};

const FIRM_NOTE_UPDATE_FIELD_LABELS: Record<string, string> = {
  note: "note",
  name: "name",
};

const AOP_UPDATE_FIELD_LABELS: Record<string, string> = {
  displayName: "display name",
  aliasName: "alias name",
  isApproved: "is approved",
  isIgnored: "is ignored",
  practiceIndustryIds: "practice industries",
};

const COMMON_UPDATE_FIELD_LABELS: Record<string, string> = {
  ...PERSON_UPDATE_FIELD_LABELS,
  ...OFFICE_UPDATE_FIELD_LABELS,
  ...DATA_ADMIN_UPDATE_FIELD_LABELS,
  ...CONTACT_UPDATE_FIELD_LABELS,
  ...FIRM_NOTE_UPDATE_FIELD_LABELS,
  ...AOP_UPDATE_FIELD_LABELS,
  description: "description",
  isSent: "sent",
  type: "type",
};

const MAX_CHANGE_VALUE_LENGTH = 80;

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  return false;
}

function isUuid(value: unknown): boolean {
  return typeof value === "string" && UUID_RE.test(value.trim());
}

function normalizeAction(action: string): "CREATE" | "UPDATE" | "DELETE" | string {
  const upper = String(action ?? "").trim().toUpperCase();
  if (upper === "CREATE" || upper === "UPDATE" || upper === "DELETE") {
    return upper;
  }
  const byLabel: Record<string, "CREATE" | "UPDATE" | "DELETE"> = {
    create: "CREATE",
    update: "UPDATE",
    delete: "DELETE",
  };
  return byLabel[String(action ?? "").trim().toLowerCase()] ?? upper;
}

export function getChangeMap(
  changes?: IAuditChange[],
): Record<string, ChangeEntry> {
  const map: Record<string, ChangeEntry> = {};
  for (const change of changes ?? []) {
    map[change.field] = { oldValue: change.oldValue, newValue: change.newValue };
  }
  return map;
}

function pickFromChanges(
  map: Record<string, ChangeEntry>,
  field: string,
  prefer: "new" | "old" = "new",
): unknown {
  const entry = map[field];
  if (!entry) {
    return undefined;
  }
  const value = prefer === "new" ? entry.newValue : entry.oldValue;
  return isEmptyValue(value) ? undefined : value;
}

function formatRecordRef(id: string | number | undefined): string {
  if (id === undefined || id === null || id === "") {
    return "";
  }
  return `(#${id})`;
}

function tailEntityId(entityId: string): string {
  const trimmed = String(entityId ?? "").trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.length > 8 ? trimmed.slice(-8) : trimmed;
}

export function getRecordId(row: FirmAuditLogNoteInput): string {
  const map = getChangeMap(row.changes);
  const candidates = [
    pickFromChanges(map, "generatedId"),
    pickFromChanges(map, "shortId"),
    pickFromChanges(map, "invoiceNumber"),
    pickFromChanges(map, "reference"),
    pickFromChanges(map, "registrationId"),
    pickFromChanges(map, "contactId"),
    pickFromChanges(map, "id"),
  ];

  for (const candidate of candidates) {
    if (candidate !== undefined && !isUuid(candidate)) {
      return String(candidate);
    }
  }

  const entityId = String(row.entityId ?? "").trim();
  if (entityId && !isUuid(entityId)) {
    return entityId;
  }
  return tailEntityId(entityId);
}

export function getFirmLabel(row: FirmAuditLogNoteInput): string {
  const modalName = String(row.firmName ?? "").trim();
  if (modalName) {
    return modalName;
  }

  const map = getChangeMap(row.changes);
  const fromChanges = pickFromChanges(map, "firmName") ?? pickFromChanges(map, "name");
  if (fromChanges && typeof fromChanges === "string" && !isUuid(fromChanges)) {
    return fromChanges.trim();
  }
  return "the firm";
}

function getEntityLabel(entity: string): string {
  const key = entity as AuditEntityTypeEnum;
  if (key in AuditEntityType) {
    return AuditEntityType[key];
  }
  return formatRoleLabel(entity) || entity || "Record";
}

function getPersonName(map: Record<string, ChangeEntry>): string {
  const name = pickFromChanges(map, "name");
  const lastName = pickFromChanges(map, "lastName");
  const parts = [name, lastName]
    .filter((p) => p != null && String(p).trim() !== "")
    .map((p) => String(p).trim());
  return parts.join(" ");
}

function getEntityName(map: Record<string, ChangeEntry>): string {
  const personName = getPersonName(map);
  if (personName) {
    return personName;
  }
  const name = pickFromChanges(map, "name");
  if (name && typeof name === "string" && !isUuid(name)) {
    return name.trim();
  }
  return "";
}

export function getBillingDocType(
  changes?: IAuditChange[],
): "payment" | "invoice" | "financialProfile" | undefined {
  const map = getChangeMap(changes);
  const raw =
    pickFromChanges(map, "type", "new") ?? pickFromChanges(map, "type", "old");
  const value = String(raw ?? "").trim().toLowerCase();
  if (value === "payment" || value === "invoice" || value === "financialProfile") {
    return value;
  }
  return undefined;
}

function isSentActivated(map: Record<string, ChangeEntry>): boolean {
  const entry = map.isSent;
  if (!entry) {
    return false;
  }
  const wasSent = entry.oldValue === true;
  const nowSent = entry.newValue === true;
  return !wasSent && nowSent;
}

function getReadableChanges(map: Record<string, ChangeEntry>): string[] {
  return Object.entries(map)
    .filter(([field]) => !NOISE_FIELDS.has(field))
    .filter(([, entry]) => {
      if (JSON.stringify(entry.oldValue) === JSON.stringify(entry.newValue)) {
        return false;
      }
      if (isEmptyValue(entry.oldValue) && isEmptyValue(entry.newValue)) {
        return false;
      }
      return true;
    })
    .map(([field]) => field);
}

function truncateChangeValue(text: string): string {
  return text.length > MAX_CHANGE_VALUE_LENGTH
    ? `${text.slice(0, MAX_CHANGE_VALUE_LENGTH)}…`
    : text;
}

function formatAuditChangeValue(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    if (value.every((item) => typeof item === "string" || typeof item === "number")) {
      const joined = value.map(String).join(", ");
      return joined ? truncateChangeValue(joined) : "—";
    }
    return truncateChangeValue(JSON.stringify(value));
  }
  if (typeof value === "object") {
    return truncateChangeValue(JSON.stringify(value));
  }
  return truncateChangeValue(String(value));
}

function getFieldLabel(
  field: string,
  labels: Record<string, string>,
): string {
  return labels[field] ?? formatRoleLabel(field) ?? field;
}

function formatUpdateChangeLines(
  map: Record<string, ChangeEntry>,
  fieldLabels: Record<string, string>,
): string[] {
  return getReadableChanges(map).map((field) => {
    const entry = map[field];
    const label = getFieldLabel(field, fieldLabels);
    return `${label}: ${formatAuditChangeValue(entry.oldValue)} → ${formatAuditChangeValue(entry.newValue)}`;
  });
}

function formatUpdateNoteWithChanges(
  headline: string,
  map: Record<string, ChangeEntry>,
  fieldLabels: Record<string, string> = COMMON_UPDATE_FIELD_LABELS,
): string {
  const changeLines = formatUpdateChangeLines(map, fieldLabels);
  if (changeLines.length === 0) {
    return headline.trim();
  }
  return [headline, ...changeLines].join("\n").trim();
}

function formatPaymentOrBillingNote(
  row: FirmAuditLogNoteInput,
  action: "CREATE" | "UPDATE" | "DELETE",
): string | null {
  const entity = String(row.entity ?? "");
  const isBillingPayment =
    entity === "Billing" && getBillingDocType(row.changes) === "payment";
  const isLegacyPayment = entity === "Payment";
  if (!isBillingPayment && !isLegacyPayment) {
    return null;
  }

  const firm = getFirmLabel(row);
  const recordId = getRecordId(row);
  const recordRef = formatRecordRef(recordId);
  const map = getChangeMap(row.changes);

  if (action === "DELETE") {
    return `Deleted Payment for record ${recordRef} of ${firm}`.trim();
  }

  if (action === "CREATE") {
    const invoiceRef =
      pickFromChanges(map, "invoiceNumber") ??
      pickFromChanges(map, "reference") ??
      recordId;
    const invoicePart = invoiceRef
      ? ` with invoice (#${invoiceRef})`
      : recordRef
        ? ` ${recordRef}`
        : "";
    return `Added new Payment${invoicePart} for ${firm}`.trim();
  }

  if (action === "UPDATE" && isSentActivated(map)) {
    return `Sent confirmation of Payment for record ${recordRef} to ${firm}`.trim();
  }

  return null;
}

function formatBillingInvoiceNote(
  row: FirmAuditLogNoteInput,
  action: "CREATE" | "UPDATE" | "DELETE",
): string | null {
  if (row.entity !== "Billing" || getBillingDocType(row.changes) !== "invoice") {
    return null;
  }

  const firm = getFirmLabel(row);
  const recordId = getRecordId(row);
  const recordRef = formatRecordRef(recordId);
  const map = getChangeMap(row.changes);
  const invoiceNumber = pickFromChanges(map, "invoiceNumber");

  if (action === "DELETE") {
    return `Deleted Invoice for record ${recordRef} of ${firm}`.trim();
  }

  if (action === "CREATE") {
    const invoiceRef = invoiceNumber
      ? formatRecordRef(String(invoiceNumber))
      : recordRef;
    return `Added new Invoice ${invoiceRef} for ${firm}`.trim();
  }

  if (action === "UPDATE" && isSentActivated(map)) {
    const ref = invoiceNumber
      ? formatRecordRef(String(invoiceNumber))
      : recordRef;
    return `Sent Invoice for record ${ref} to ${firm}`.trim();
  }

  return null;
}

function formatInvoiceHistoryNote(
  row: FirmAuditLogNoteInput,
  action: string,
): string | null {
  if (row.entity !== "InvoiceHistory" || action !== "CREATE") {
    return null;
  }
  const firm = getFirmLabel(row);
  const map = getChangeMap(row.changes);
  const regId =
    pickFromChanges(map, "registrationId") ??
    pickFromChanges(map, "contactId") ??
    getRecordId(row);
  const recordRef = formatRecordRef(String(regId));
  return `Sent Invoice Email for record ${recordRef} for ${firm}`.trim();
}

function formatFirmManagementNote(
  row: FirmAuditLogNoteInput,
  action: "CREATE" | "UPDATE" | "DELETE",
): string | null {
  const entity = String(row.entity ?? "");
  const firm = getFirmLabel(row);
  const map = getChangeMap(row.changes);
  const name = getEntityName(map);
  const recordId = getRecordId(row);
  const recordRef = formatRecordRef(recordId);

  switch (entity) {
    case "Person": {
      const displayName = name || "Professional";
      if (action === "CREATE") {
        return `Added new Professional ${displayName} ${recordRef} for ${firm}`.trim();
      }
      if (action === "UPDATE") {
        return formatUpdateNoteWithChanges(
          `Updated Professional ${displayName} ${recordRef}`.trim(),
          map,
          PERSON_UPDATE_FIELD_LABELS,
        );
      }
      if (action === "DELETE") {
        return `Deleted Professional ${recordRef} from ${firm}`.trim();
      }
      break;
    }
    case "Firm": {
      const firmName = name || firm;
      if (action === "CREATE") {
        return `Added new Firm ${firmName}`.trim();
      }
      if (action === "UPDATE") {
        return formatUpdateNoteWithChanges(
          `Updated Firm ${firmName}`.trim(),
          map,
          FIRM_UPDATE_FIELD_LABELS,
        );
      }
      if (action === "DELETE") {
        return `Deleted Firm ${firmName}`.trim();
      }
      break;
    }
    case "Office": {
      const officeName = name || "Office";
      if (action === "CREATE") {
        return `Added new Office ${officeName} ${recordRef} for ${firm}`.trim();
      }
      if (action === "UPDATE") {
        return formatUpdateNoteWithChanges(
          `Updated Office ${officeName} ${recordRef}`.trim(),
          map,
          OFFICE_UPDATE_FIELD_LABELS,
        );
      }
      if (action === "DELETE") {
        return `Deleted Office ${officeName} from ${firm}`.trim();
      }
      break;
    }
    case "DataAdmin": {
      const adminName = name || "Data Admin";
      if (action === "CREATE") {
        return `Added new Data Admin ${adminName} for ${firm}`.trim();
      }
      if (action === "UPDATE") {
        return formatUpdateNoteWithChanges(
          `Updated Data Admin ${adminName}`.trim(),
          map,
          DATA_ADMIN_UPDATE_FIELD_LABELS,
        );
      }
      if (action === "DELETE") {
        return `Deleted Data Admin ${adminName}`.trim();
      }
      break;
    }
    case "FirmContact":
    case "OfficeContact":
    case "OfficeMainContact": {
      const label = getEntityLabel(entity);
      if (action === "CREATE") {
        return `Added ${label} for ${firm}`.trim();
      }
      if (action === "UPDATE") {
        return formatUpdateNoteWithChanges(
          `Updated ${label} for ${firm}`.trim(),
          map,
          CONTACT_UPDATE_FIELD_LABELS,
        );
      }
      if (action === "DELETE") {
        return `Deleted ${label} from ${firm}`.trim();
      }
      break;
    }
    case "FirmNote": {
      if (action === "CREATE") {
        return `Added Firm Note for ${firm}`.trim();
      }
      if (action === "UPDATE") {
        return formatUpdateNoteWithChanges(
          `Updated Firm Note for ${firm}`.trim(),
          map,
          FIRM_NOTE_UPDATE_FIELD_LABELS,
        );
      }
      if (action === "DELETE") {
        return `Deleted Firm Note from ${firm}`.trim();
      }
      break;
    }
    case "AreaOfPractice":
    case "RepresentativeClient": {
      const label = getEntityLabel(entity);
      const itemName = name ? ` ${name}` : "";
      if (action === "CREATE") {
        return `Added ${label}${itemName} for ${firm}`.trim();
      }
      if (action === "UPDATE") {
        return formatUpdateNoteWithChanges(
          `Updated ${label}${itemName} for ${firm}`.trim(),
          map,
          entity === "AreaOfPractice"
            ? AOP_UPDATE_FIELD_LABELS
            : COMMON_UPDATE_FIELD_LABELS,
        );
      }
      if (action === "DELETE") {
        return `Deleted ${label}${itemName} from ${firm}`.trim();
      }
      break;
    }
    default:
      return null;
  }

  return null;
}

function formatFallbackNote(
  row: FirmAuditLogNoteInput,
  action: "CREATE" | "UPDATE" | "DELETE",
): string {
  const entityLabel = getEntityLabel(String(row.entity ?? ""));
  const firm = getFirmLabel(row);
  const recordId = getRecordId(row);
  const recordRef = formatRecordRef(recordId);
  const map = getChangeMap(row.changes);

  if (action === "CREATE") {
    return `Added new ${entityLabel} for record ${recordRef} of ${firm}`.trim();
  }
  if (action === "DELETE") {
    return `Deleted ${entityLabel} for record ${recordRef} of ${firm}`.trim();
  }

  return formatUpdateNoteWithChanges(
    `Updated ${entityLabel} for record ${recordRef}`.trim(),
    map,
    COMMON_UPDATE_FIELD_LABELS,
  );
}

export function formatFirmAuditLogNote(row: FirmAuditLogNoteInput): string {
  const action = normalizeAction(row.action);
  if (action !== "CREATE" && action !== "UPDATE" && action !== "DELETE") {
    return "—";
  }

  const special =
    formatPaymentOrBillingNote(row, action) ??
    formatBillingInvoiceNote(row, action) ??
    formatInvoiceHistoryNote(row, action) ??
    formatFirmManagementNote(row, action);

  if (special) {
    return special;
  }

  if (!row.changes?.length && !row.entityId) {
    return "—";
  }

  return formatFallbackNote(row, action);
}
